import React, { useState, useEffect } from 'react';

import { 
  XMarkIcon, 
  PlusIcon, 
  TrashIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import { Client, Invoice, ProfessionalExpense } from '../types';
import { formatIban, normalizeIban, validateSpanishIban } from '../utils/spanishFiscal';
import { LinkedExpensesSection } from './LinkedExpensesSection';
import { apiService } from '../services/apiService';
import type { BusinessLine, PaymentMethodConfig, Project, CompanyPaymentSettings, InvoiceGroup } from '../types';
import { toast } from 'sonner';

interface InvoiceFormProps {
  onClose: () => void;
  onSubmit: (invoice: Omit<Invoice, 'id' | 'createdAt' | 'updatedAt'>) => void;
  clients: Client[];
  nextInvoiceNumber: number;
  setNextInvoiceNumber: (number: number) => void;
  editingInvoice?: Invoice | null;
  onCreateClient?: () => void;
  onQuickCreateClient?: (payload: { name: string; cif?: string; address?: string; city?: string; email?: string; phone?: string; }) => Promise<Client | null>;
  companyId?: string;
  projects?: Project[];
  defaultProjectId?: string | null;
  paymentSettings?: CompanyPaymentSettings;
  invoiceConfig?: {
    invoicePrefix: string;
    currentYear: number;
    lastInvoiceNumber: number;
  };
}

export const InvoiceForm: React.FC<InvoiceFormProps> = ({
  onClose,
  onSubmit,
  clients,
  nextInvoiceNumber,
  setNextInvoiceNumber,
  editingInvoice,
  onCreateClient,
  onQuickCreateClient,
  invoiceConfig,
  companyId,
  projects = [],
  defaultProjectId = null,
  paymentSettings
}) => {
  console.log('InvoiceForm received invoiceConfig:', invoiceConfig);
  const templateOptions = [
    { value: 'standard', label: 'Profesional (moderno)' },
    { value: 'expense', label: 'Gastos (modelo clasico)' },
    { value: 'expense_suplidos', label: 'Gastos suplidos (modelo clasico)' },
    { value: 'clean', label: 'Limpia' },
    { value: 'corporate', label: 'Corporativa' }
  ] as const;
  const designConfig = (() => {
    try {
      const saved = localStorage.getItem('invoiceDesignConfig');
      const parsed = saved ? JSON.parse(saved) : null;
      const enabled = Array.isArray(parsed?.enabledTemplates)
        ? (parsed.enabledTemplates as Array<'standard' | 'expense' | 'expense_suplidos' | 'clean' | 'corporate'>)
        : templateOptions.map((t) => t.value);
      const fallbackDefault = enabled.includes(parsed?.defaultTemplate)
        ? parsed.defaultTemplate
        : (enabled[0] || 'standard');
      return {
        defaultTemplate: fallbackDefault as 'standard' | 'expense' | 'expense_suplidos' | 'clean' | 'corporate',
        enabledTemplates: enabled
      };
    } catch {
      return {
        defaultTemplate: 'standard' as const,
        enabledTemplates: templateOptions.map((t) => t.value)
      };
    }
  })();
  const enabledTemplateOptions = templateOptions.filter((t) => designConfig.enabledTemplates.includes(t.value));
  const defaultTemplateType = (designConfig.defaultTemplate || 'standard') as 'standard' | 'expense' | 'expense_suplidos' | 'clean' | 'corporate';
  
  const [formData, setFormData] = useState({
    number: nextInvoiceNumber,
    date: new Date().toISOString().split('T')[0],
    clientId: '',
    description: '',
    unitPrice: '',
    quantity: 1,
    subtotal: 0,
    suplidosAmount: 0,
    ivaPercentage: 21,
    ivaAmount: 0,
    irpfPercentage: 15,
    irpfAmount: 0,
    total: 0,
    status: 'draft' as 'draft' | 'sent' | 'paid' | 'overdue',
    dueDate: '',
    paymentMethod: 'transfer' as 'transfer' | 'card' | 'cash' | 'direct_debit' | 'bizum',
    paymentTermsDays: 30,
    bankIban: '',
    templateType: defaultTemplateType as 'standard' | 'expense' | 'expense_suplidos' | 'clean' | 'corporate',
    expenseCategory: 'other' as 'travel' | 'supplies' | 'services' | 'fees' | 'other',
    expenseReference: '',
    deductibleExpense: true,
    alreadyPresentedToAeat: false,
    alreadyPresentedAt: '',
    alreadyPresentedRef: '',
    notes: '',
    projectId: '' as string,
    paymentMethodId: '' as string,
    paymentNotes: ''
  });
  const [dueDateTouched, setDueDateTouched] = useState(false);
  const [businessLines, setBusinessLines] = useState<BusinessLine[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodConfig[]>([]);
  const [invoiceGroups, setInvoiceGroups] = useState<InvoiceGroup[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);

  useEffect(() => {
    (async () => {
      const [bl, pm, gr] = await Promise.all([
        apiService.getBusinessLines(),
        apiService.getPaymentMethods(),
        apiService.getInvoiceGroups()
      ]);
      if (!bl.error && bl.data?.businessLines) setBusinessLines(bl.data.businessLines);
      if (!pm.error && pm.data?.paymentMethods) setPaymentMethods(pm.data.paymentMethods);
      if (!gr.error && gr.data?.groups) setInvoiceGroups(gr.data.groups);
    })();
  }, []);

  useEffect(() => {
    if (!formData.projectId) return;
    const line = businessLines.find((l) => l.id === formData.projectId);
    if (!line?.defaultPaymentMethodId) return;
    const pm = paymentMethods.find((m) => m.id === line.defaultPaymentMethodId);
    if (!pm) return;
    setFormData((prev) => ({
      ...prev,
      paymentMethodId: pm.id,
      paymentMethod: pm.kind as typeof prev.paymentMethod,
      paymentNotes: pm.instructionsText || prev.paymentNotes,
      bankIban: pm.kind === 'transfer' && pm.config?.iban ? pm.config.iban : prev.bankIban
    }));
  }, [formData.projectId, businessLines, paymentMethods]);

  const [items, setItems] = useState([
    { description: '', unitPrice: '', quantity: 1, subtotal: 0 }
  ]);
  const [linkedExpenseIds, setLinkedExpenseIds] = useState<string[]>([]);
  const [linkedExpenses, setLinkedExpenses] = useState<ProfessionalExpense[]>([]);
  const [manualSuplidosAmount, setManualSuplidosAmount] = useState<number | null>(null);

  const isExpenseNoSuplidos = formData.templateType === 'expense';
  const isExpenseSuplidos = formData.templateType === 'expense_suplidos';
  const showSuplidosField = !isExpenseNoSuplidos;

  // Función para parsear números decimales correctamente
  const parseDecimal = (value: string | number): number => {
    if (typeof value === 'number') return value;
    if (!value || value.trim() === '') return 0;
    // Reemplazar comas por puntos y parsear
    const cleanValue = value.replace(',', '.');
    const parsed = parseFloat(cleanValue);
    return isNaN(parsed) ? 0 : parsed;
  };

  // Función para formatear números con 3 decimales
  const formatDecimal = (value: number): string => {
    return value.toFixed(3).replace(/\.?0+$/, ''); // Elimina ceros innecesarios al final
  };

  useEffect(() => {
    setFormData(prev => ({ ...prev, number: nextInvoiceNumber }));
  }, [nextInvoiceNumber]);

  // Cargar datos de la factura si se está editando
  useEffect(() => {
    if (editingInvoice) {
      const safeDate = new Date((editingInvoice as any).date);
      const safeDue = editingInvoice.dueDate ? new Date((editingInvoice as any).dueDate) : undefined;
      setFormData({
        number: editingInvoice.number,
        date: isNaN(safeDate.getTime()) ? new Date().toISOString().split('T')[0] : safeDate.toISOString().split('T')[0],
        clientId: editingInvoice.clientId,
        description: editingInvoice.description,
        unitPrice: formatDecimal(editingInvoice.unitPrice),
        quantity: editingInvoice.quantity,
        subtotal: editingInvoice.subtotal,
        suplidosAmount: editingInvoice.suplidosAmount || 0,
        ivaPercentage: editingInvoice.ivaPercentage,
        ivaAmount: editingInvoice.ivaAmount,
        irpfPercentage: editingInvoice.irpfPercentage,
        irpfAmount: editingInvoice.irpfAmount,
        total: editingInvoice.total,
        status: editingInvoice.status as 'draft' | 'sent' | 'paid' | 'overdue',
        dueDate: safeDue && !isNaN(safeDue.getTime()) ? safeDue.toISOString().split('T')[0] : '',
        paymentMethod: editingInvoice.paymentMethod || 'transfer',
        paymentTermsDays: editingInvoice.paymentTermsDays ?? 30,
        bankIban: editingInvoice.bankIban ? formatIban(editingInvoice.bankIban) : '',
        templateType: (editingInvoice.templateType || defaultTemplateType) as 'standard' | 'expense' | 'expense_suplidos' | 'clean' | 'corporate',
        expenseCategory: editingInvoice.expenseCategory || 'other',
        expenseReference: editingInvoice.expenseReference || '',
        deductibleExpense: editingInvoice.deductibleExpense ?? true,
        alreadyPresentedToAeat: !!(editingInvoice as any).alreadyPresentedToAeat,
        alreadyPresentedAt: (editingInvoice as any).alreadyPresentedAt
          ? new Date((editingInvoice as any).alreadyPresentedAt).toISOString().split('T')[0]
          : '',
        alreadyPresentedRef: (editingInvoice as any).alreadyPresentedRef || '',
        notes: editingInvoice.notes || '',
        projectId: (editingInvoice as any).projectId || '',
        paymentMethodId: (editingInvoice as any).paymentMethodId || '',
        paymentNotes: (editingInvoice as any).paymentNotes || ''
      });
      const editGroups = (editingInvoice as any).groups as InvoiceGroup[] | undefined;
      const editGroupIds = (editingInvoice as any).groupIds as string[] | undefined;
      setSelectedGroupIds(
        editGroupIds?.length ? editGroupIds : (editGroups || []).map((g) => g.id)
      );
      setDueDateTouched(true);

      setItems([{
        description: editingInvoice.description,
        unitPrice: formatDecimal(editingInvoice.unitPrice),
        quantity: editingInvoice.quantity,
        subtotal: Number(editingInvoice.subtotal || 0)
      }]);
    }
  }, [editingInvoice]);

  const calculateTotals = () => {
    const servicesSubtotal = items.reduce((sum, item) => {
      const unitPrice = parseDecimal(item.unitPrice);
      const itemSubtotal = unitPrice * item.quantity;
      return sum + itemSubtotal;
    }, 0);

    const linkedReimbursable = linkedExpenses
      .filter((e) => e.expenseKind === 'no_suplido')
      .reduce((s, e) => s + Number(e.baseImponible || 0), 0);
    const linkedSuplidos = linkedExpenses
      .filter((e) => e.expenseKind === 'suplido')
      .reduce((s, e) => s + Number(e.total || 0), 0);

    const subtotal = servicesSubtotal + linkedReimbursable;
    const manualSuplidos =
      manualSuplidosAmount != null ? manualSuplidosAmount : Number(formData.suplidosAmount || 0);
    const suplidosAmount = manualSuplidos + linkedSuplidos;
    const ivaAmount = subtotal * (formData.ivaPercentage / 100);
    const irpfAmount = subtotal * (formData.irpfPercentage / 100);
    const total = subtotal + ivaAmount - irpfAmount + suplidosAmount;

    setFormData(prev => ({
      ...prev,
      subtotal,
      suplidosAmount,
      ivaAmount,
      irpfAmount,
      total
    }));
  };

  useEffect(() => {
    calculateTotals();
  }, [items, formData.ivaPercentage, formData.irpfPercentage, formData.suplidosAmount, linkedExpenses, manualSuplidosAmount]);

  useEffect(() => {
    if (dueDateTouched) return;
    const terms = Number(formData.paymentTermsDays || 0);
    if (!formData.date || terms <= 0) {
      setFormData(prev => ({ ...prev, dueDate: '' }));
      return;
    }
    const base = new Date(formData.date);
    if (isNaN(base.getTime())) return;
    base.setDate(base.getDate() + terms);
    const nextDueDate = base.toISOString().split('T')[0];
    setFormData(prev => ({ ...prev, dueDate: nextDueDate }));
  }, [formData.date, formData.paymentTermsDays, dueDateTouched]);

  const handleItemChange = (index: number, field: string, value: string | number) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    
    if (field === 'unitPrice' || field === 'quantity') {
      const unitPrice = field === 'unitPrice' ? parseDecimal(value) : parseDecimal(newItems[index].unitPrice);
      const quantity = field === 'quantity' ? Number(value) : newItems[index].quantity;
      newItems[index].subtotal = unitPrice * quantity;
    }
    
    setItems(newItems);
  };

  const addItem = () => {
    setItems([...items, { description: '', unitPrice: '', quantity: 1, subtotal: 0 }]);
  };

  const removeItem = (index: number) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const selectedClient = clients.find(c => c.id === formData.clientId);
    if (!selectedClient) {
      toast.error('Por favor selecciona un cliente');
      return;
    }

    if (!selectedClient.email || !selectedClient.email.trim()) {
      toast.error('El cliente debe tener email para poder enviar la factura');
      return;
    }

    const normalizedIban = normalizeIban(formData.bankIban || '');
    if (normalizedIban) {
      const ibanValidation = validateSpanishIban(normalizedIban);
      if (!ibanValidation.valid) {
        toast.error(ibanValidation.message || 'IBAN no valido');
        return;
      }
    }

    if (formData.templateType === 'expense_suplidos' && Number(formData.suplidosAmount || 0) <= 0) {
      toast.error('Las facturas de gastos suplidos deben incluir un importe de suplidos mayor que 0');
      return;
    }
    if (formData.templateType === 'expense' && Number(formData.suplidosAmount || 0) > 0) {
      toast.error('Las facturas de gastos sin suplidos no pueden incluir importe de suplidos');
      return;
    }

    const normalizedSuplidos = formData.templateType === 'expense' ? 0 : Number(formData.suplidosAmount || 0);

    const invoiceData = {
      ...formData,
      companyId: companyId || '',
      date: new Date(formData.date),
      dueDate: formData.dueDate ? new Date(formData.dueDate) : undefined,
      projectId: formData.projectId || undefined,
      groupIds: selectedGroupIds,
      paymentMethodId: formData.paymentMethodId || undefined,
      paymentNotes: formData.paymentNotes || undefined,
      paymentMethod: formData.paymentMethod,
      paymentTermsDays: Number(formData.paymentTermsDays || 0),
      bankIban: normalizedIban || undefined,
      templateType: formData.templateType,
      expenseCategory: formData.expenseCategory,
      expenseReference: formData.expenseReference.trim() || undefined,
      deductibleExpense: !!formData.deductibleExpense,
      suplidosAmount: normalizedSuplidos,
      client: selectedClient,
      unitPrice: parseDecimal(formData.unitPrice),
      items: items.filter(item => item.description.trim() !== '').map(item => ({
        ...item,
        unitPrice: parseDecimal(item.unitPrice),
        subtotal: parseDecimal(item.unitPrice) * item.quantity
      })),
      alreadyPresentedToAeat: !!formData.alreadyPresentedToAeat,
      alreadyPresentedAt:
        formData.alreadyPresentedToAeat && formData.alreadyPresentedAt
          ? new Date(formData.alreadyPresentedAt)
          : undefined,
      alreadyPresentedRef: formData.alreadyPresentedToAeat ? (formData.alreadyPresentedRef || undefined) : undefined,
      linkedExpenseIds
    };

    onSubmit(invoiceData);
    setNextInvoiceNumber(nextInvoiceNumber + 1);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            {editingInvoice ? 'Editar Factura' : 'Nueva Factura'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Número de Factura *
              </label>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500 font-mono">
                  {invoiceConfig ? `${invoiceConfig.invoicePrefix}-${invoiceConfig.currentYear}-` : ''}
                </span>
                <input
                  required
                  type="number"
                  value={formData.number}
                  onChange={(e) => setFormData({...formData, number: parseInt(e.target.value) || 0})}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  placeholder="001"
                  min="1"
                />
              </div>
              {invoiceConfig && (
                <p className="text-xs text-gray-500 mt-1">
                  Formato: {invoiceConfig.invoicePrefix}-{invoiceConfig.currentYear}-{String(formData.number).padStart(3, '0')}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fecha *
              </label>
              <input
                type="date"
                required
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  Cliente *
                </label>
                {onCreateClient && (
                  <button
                    type="button"
                    onClick={onCreateClient}
                    className="flex items-center text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    <UserGroupIcon className="h-4 w-4 mr-1" />
                    Nuevo Cliente
                  </button>
                )}
              </div>
              <select
                required
                value={formData.clientId}
                onChange={(e) => setFormData({...formData, clientId: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Seleccionar cliente</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>

               {/* Creación rápida inline */}
               {onQuickCreateClient && (
                 <div className="mt-2 grid grid-cols-1 md:grid-cols-4 gap-2 p-2 border rounded-md bg-gray-50">
                   <input
                     data-field="name"
                     type="text"
                     placeholder="Nombre completo / Razón social"
                     className="px-2 py-1 border rounded"
                     onKeyDown={async (e) => {
                       if (e.key === 'Enter') {
                         e.preventDefault();
                         const name = (e.target as HTMLInputElement).value.trim();
                         const cifInput = (e.currentTarget.parentElement?.querySelector('input[data-field="cif"]') as HTMLInputElement);
                         const cif = cifInput?.value.trim();
                         const emailInput = (e.currentTarget.parentElement?.querySelector('input[type="email"]') as HTMLInputElement);
                         const email = emailInput?.value.trim();
                         const telInput = (e.currentTarget.parentElement?.querySelector('input[type="tel"]') as HTMLInputElement);
                         const phone = telInput?.value.trim();
                         if (!name) return;
                         const created = await onQuickCreateClient!({ name, cif, email, phone });
                         if (created) {
                           setFormData(prev => ({ ...prev, clientId: created.id }));
                           (e.target as HTMLInputElement).value = '';
                           if (cifInput) cifInput.value = '';
                           if (emailInput) emailInput.value = '';
                           if (telInput) telInput.value = '';
                         }
                       }
                     }}
                   />
                   <input data-field="cif" type="text" placeholder="CIF/NIF" className="px-2 py-1 border rounded" />
                   <input type="email" placeholder="Email" className="px-2 py-1 border rounded" />
                   <input type="tel" placeholder="Teléfono" className="px-2 py-1 border rounded" />
                   <p className="md:col-span-4 text-xs text-gray-500">Pulsa Enter en “Nombre” para crear rápido (opcional si ya existe el cliente).</p>
                 </div>
               )}
            </div>
          </div>

          {/* Invoice Items */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">Conceptos</h3>
              <button
                type="button"
                onClick={addItem}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Añadir Concepto
              </button>
            </div>

            {items.map((item, index) => (
              <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 border rounded-lg">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descripción
                  </label>
                  <input
                    type="text"
                    value={item.description}
                    onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Descripción del servicio"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Precio Unitario
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={item.unitPrice}
                    onChange={(e) => handleItemChange(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div className="flex items-end space-x-2">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Cantidad
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value) || 1)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  {items.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      className="p-2 text-red-600 hover:text-red-800"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <div className="md:col-span-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subtotal
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={Number(item.subtotal || 0).toFixed(2)}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Tax Settings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Configuración de Impuestos</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    IVA (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.ivaPercentage}
                    onChange={(e) => setFormData({...formData, ivaPercentage: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    IRPF (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.irpfPercentage}
                    onChange={(e) => setFormData({...formData, irpfPercentage: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Resumen</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span>{formData.subtotal.toFixed(2)} €</span>
                </div>
                {showSuplidosField && Number(formData.suplidosAmount || 0) > 0 && (
                <div className="flex justify-between">
                  <span>{isExpenseSuplidos ? 'Suplidos (repercutidos):' : 'Suplidos:'}</span>
                  <span>{Number(formData.suplidosAmount || 0).toFixed(2)} €</span>
                </div>
                )}
                <div className="flex justify-between">
                  <span>IVA ({formData.ivaPercentage}%):</span>
                  <span>{formData.ivaAmount.toFixed(2)} €</span>
                </div>
                <div className="flex justify-between">
                  <span>IRPF ({formData.irpfPercentage}%):</span>
                  <span>{formData.irpfAmount.toFixed(2)} €</span>
                </div>
                <div className="flex justify-between font-medium text-lg border-t pt-2">
                  <span>Total:</span>
                  <span>{formData.total.toFixed(2)} €</span>
                </div>
              </div>
            </div>
          </div>

          {showSuplidosField && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {isExpenseSuplidos ? 'Suplidos repercutidos (obligatorio)' : 'Suplidos repercutidos (opcional)'}
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              required={isExpenseSuplidos}
              value={formData.suplidosAmount}
              onChange={(e) => {
                const val = parseDecimal(e.target.value);
                setManualSuplidosAmount(val);
                setFormData({ ...formData, suplidosAmount: val });
              }}
              className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              {isExpenseSuplidos
                ? 'Importe de gastos suplidos repercutidos al cliente. No forman parte de la base imponible ni llevan IVA ni IRPF.'
                : 'Importe opcional de gastos repercutidos. No forman parte de la base imponible ni llevan IVA ni IRPF.'}
            </p>
          </div>
          )}
          {isExpenseNoSuplidos && (
            <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
              Factura de gastos sin suplidos: todos los importes van en las lineas de servicio y entran en la base imponible.
            </p>
          )}

          {!editingInvoice && (
            <LinkedExpensesSection
              selectedIds={linkedExpenseIds}
              onChange={(ids, exps) => {
                setLinkedExpenseIds(ids);
                setLinkedExpenses(exps);
              }}
            />
          )}

          {(formData.templateType === 'expense' || formData.templateType === 'expense_suplidos') && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Categoria de gasto
                </label>
                <select
                  value={formData.expenseCategory}
                  onChange={(e) => setFormData({ ...formData, expenseCategory: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="travel">Desplazamiento</option>
                  <option value="supplies">Material / suministros</option>
                  <option value="services">Servicios de terceros</option>
                  <option value="fees">Tasas / honorarios</option>
                  <option value="other">Otros</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Referencia de justificante
                </label>
                <input
                  type="text"
                  value={formData.expenseReference}
                  onChange={(e) => setFormData({ ...formData, expenseReference: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Ticket/Doc interno"
                />
              </div>
              <div className="flex items-end">
                <label className="inline-flex items-center text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={formData.deductibleExpense}
                    onChange={(e) => setFormData({ ...formData, deductibleExpense: e.target.checked })}
                    className="mr-2"
                  />
                  Gasto deducible
                </label>
              </div>
            </div>
          )}

          {/* Additional Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fecha de Vencimiento
              </label>
              <input
                type="date"
                value={formData.dueDate}
                onChange={(e) => {
                  setDueDateTouched(true);
                  setFormData({...formData, dueDate: e.target.value});
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Estado
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({...formData, status: e.target.value as any})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="draft">Borrador</option>
                <option value="sent">Enviada</option>
                <option value="paid">Pagada</option>
                <option value="overdue">Vencida</option>
              </select>
            </div>
          </div>

          <div className="border rounded-lg p-3 bg-gray-50">
            <label className="inline-flex items-center text-sm text-gray-800">
              <input
                type="checkbox"
                checked={!!formData.alreadyPresentedToAeat}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    alreadyPresentedToAeat: e.target.checked,
                    alreadyPresentedAt: e.target.checked ? prev.alreadyPresentedAt : '',
                    alreadyPresentedRef: e.target.checked ? prev.alreadyPresentedRef : ''
                  }))
                }
                className="mr-2"
              />
              Ya presentada a Hacienda (factura histórica)
            </label>
            <p className="mt-1 text-xs text-gray-600">
              Si activas esto, la plataforma la guardará solo para control y bloqueará AEAT/VeriFactu para evitar duplicidades.
            </p>
            {formData.alreadyPresentedToAeat ? (
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Fecha de presentación (opcional)
                  </label>
                  <input
                    type="date"
                    value={formData.alreadyPresentedAt}
                    onChange={(e) => setFormData((prev) => ({ ...prev, alreadyPresentedAt: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Referencia/CSV (opcional)
                  </label>
                  <input
                    type="text"
                    value={formData.alreadyPresentedRef}
                    onChange={(e) => setFormData((prev) => ({ ...prev, alreadyPresentedRef: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    placeholder="CSV / justificante"
                  />
                </div>
              </div>
            ) : null}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Línea de negocio</label>
              <select
                value={formData.projectId}
                onChange={(e) => setFormData({ ...formData, projectId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">— Sin línea —</option>
                {businessLines.map((l) => (
                  <option key={l.id} value={l.id}>{l.categoryLabel || l.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Grupos de factura</label>
              <div className="flex flex-wrap gap-2 min-h-[42px] p-2 border border-gray-300 rounded-md">
                {invoiceGroups.length === 0 ? (
                  <span className="text-xs text-gray-500">Cree grupos en Configuración → Grupos de facturas</span>
                ) : (
                  invoiceGroups.map((g) => {
                    const active = selectedGroupIds.includes(g.id);
                    return (
                      <button
                        key={g.id}
                        type="button"
                        onClick={() =>
                          setSelectedGroupIds((prev) =>
                            active ? prev.filter((id) => id !== g.id) : [...prev, g.id]
                          )
                        }
                        className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                          active ? 'text-white border-transparent' : 'bg-white text-gray-700 border-gray-300 hover:border-violet-400'
                        }`}
                        style={active ? { backgroundColor: g.color || '#6366f1', borderColor: g.color || '#6366f1' } : undefined}
                      >
                        {g.name}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Metodo de pago
              </label>
              <select
                value={formData.paymentMethodId || formData.paymentMethod}
                onChange={(e) => {
                  const pm = paymentMethods.find((m) => m.id === e.target.value);
                  if (pm) {
                    setFormData({
                      ...formData,
                      paymentMethodId: pm.id,
                      paymentMethod: pm.kind as any,
                      paymentNotes: pm.instructionsText || '',
                      bankIban: pm.kind === 'transfer' && pm.config?.iban ? pm.config.iban : formData.bankIban
                    });
                  } else {
                    setFormData({ ...formData, paymentMethodId: '', paymentMethod: e.target.value as any });
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              >
                {paymentMethods.length ? paymentMethods.map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                )) : (
                  <>
                    <option value="transfer">Transferencia bancaria</option>
                    <option value="card">Tarjeta</option>
                    <option value="cash">Efectivo</option>
                    <option value="direct_debit">Domiciliacion</option>
                    <option value="bizum">Bizum</option>
                  </>
                )}
              </select>
              {formData.paymentMethod === 'transfer' || paymentMethods.find((m) => m.id === formData.paymentMethodId)?.kind === 'transfer' ? (
                <input
                  type="text"
                  placeholder="IBAN (opcional)"
                  value={formData.bankIban}
                  onChange={(e) => setFormData({ ...formData, bankIban: e.target.value })}
                  className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              ) : null}
              {(formData.paymentMethod === 'bizum' || paymentMethods.find((m) => m.id === formData.paymentMethodId)?.kind === 'bizum') ? (
                <input
                  type="text"
                  placeholder="Teléfono Bizum / notas"
                  value={formData.paymentNotes}
                  onChange={(e) => setFormData({ ...formData, paymentNotes: e.target.value })}
                  className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              ) : null}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Plazo de pago (dias)
              </label>
              <input
                type="number"
                min="0"
                max="365"
                value={formData.paymentTermsDays}
                onChange={(e) => {
                  setDueDateTouched(false);
                  setFormData({...formData, paymentTermsDays: parseInt(e.target.value, 10) || 0});
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Plantilla de factura
              </label>
              <select
                value={formData.templateType}
                onChange={(e) => {
                  const next = e.target.value as typeof formData.templateType;
                  setFormData({
                    ...formData,
                    templateType: next,
                    suplidosAmount: next === 'expense' ? 0 : formData.suplidosAmount
                  });
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              >
                {enabledTemplateOptions.map((template) => (
                  <option key={template.value} value={template.value}>
                    {template.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                IBAN de cobro (opcional)
              </label>
              <input
                type="text"
                value={formData.bankIban}
                onChange={(e) => setFormData({...formData, bankIban: formatIban(e.target.value)})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 font-mono"
                placeholder="ES00 0000 0000 0000 0000 0000"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notas
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              placeholder="Notas adicionales..."
            />
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
            >
              {editingInvoice ? 'Guardar cambios' : 'Crear Factura'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}; 